/**
 * Shared motion vocabulary for product UI transitions.
 * Durations stay in the 100–250ms product band; exits are ~75% of enters.
 * Prefer these tokens over one-off magic numbers in shell / feature code.
 *
 * Opacity only (no y travel).
 * - Routes: `pageTransition` + `mode="wait"` (full pages must not stack).
 * - MC panels / opening: `panelCrossfade` stacked absolute (enter+exit
 *   together so the frame never blanks between workshops / Back / Close).
 */

export const easeOutExpo = [0.16, 1, 0.3, 1] as const;
export const easeOutQuint = [0.22, 1, 0.36, 1] as const;
export const easeInExpo = [0.7, 0, 0.84, 0] as const;

/** Instant feedback (press, toggle). */
export const DURATION_PRESS = 0.1;
/** State change (overlay, menu, selection settle). */
export const DURATION_STATE = 0.15;
/** View / route enter. */
export const DURATION_ENTER = 0.18;
/** View / route / modal exit (~75% of enter). */
export const DURATION_EXIT = 0.12;
/** MC panel stacked crossfade. */
export const DURATION_PANEL = 0.2;

/** Quiet opacity fade for full route swaps (`mode="wait"`). */
export const pageTransition = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: DURATION_ENTER, ease: easeOutExpo },
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATION_EXIT, ease: easeInExpo },
  },
} as const;

/**
 * Stacked MC crossfade. Pair with `absolute inset-0` and AnimatePresence
 * default (sync) mode so outgoing and incoming overlap — no blank gap.
 */
export const panelCrossfade = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: DURATION_PANEL, ease: easeOutExpo },
  },
  exit: {
    opacity: 0,
    transition: { duration: DURATION_PANEL * 0.75, ease: easeInExpo },
  },
} as const;

/** Instant cut for prefers-reduced-motion (still a tiny fade so state is clear). */
export const pageTransitionReduced = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.08, ease: easeOutExpo },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.06, ease: easeInExpo },
  },
} as const;
